import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  TouchableWithoutFeedback,
  KeyboardAvoidingView,
  Keyboard,
  Modal,
  Pressable,
  Animated,
  ActivityIndicator,
} from 'react-native';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP } from '../../../../web/sheet';
import {
  v2CreateDeposit,
  v2GetMyBalances,
  v2GetMinDepositAmount,
  v2GetDepositProducts,
  v2GetRates,
} from '../../../services/v2/api';
import type {
  V2Balance,
  V2CreateDepositRequest,
  V2DepositProduct,
  V2RateInfo,
} from '../../../types/v2';
import TabBar from '../../../components/TabBar';
import AmountCaret from '../../../components/UIComponents/AmountCaret';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import { compareByNetwork } from '../../../utils/networkOrder';
import { formatAmount, parseAmount, numberToAmountDisplay, formatTokenAmount } from '../../../utils/amountFormat';
import { MAX_AMOUNT_DECIMALS, toRequestAmount } from '../../../utils/moneyAmount';
import { toUsdtAmount, formatUsdtAmount, isUsdPegged } from '../../../utils/usdtEquivalent';
import { minimumInToken } from '../minimumAmountRule';
import NumericKeypad from '../../../components/NumericKeypad';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { usePIN } from '../../../contexts/PINContext';
import { formatDepositMeta } from '../../../utils/depositMeta';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

const TERM_OPTIONS: number[] = [3, 6, 12];

// Picker-sheet top inset — sheet «упирается» верхом сразу под
// SimpleHeader'ом (без gap'а contentContainer'а). Sheet inline
// (absolute-positioned внутри SafeAreaView-wrapped root'а), Y=0 уже
// под status bar'ом; значение = высота SimpleHeader'а от safeArea top.
//
// SimpleHeader height:
//   paddingTop 8 + minHeight 48 + paddingBottom (20 iOS / 8 Android)
//   ≈ 68 iOS / 56 Android
const PICKER_TOP_INSET = Platform.OS === 'android' ? 56 : 68;

const formatBalance = (amount: string): string => {
  const num = parseFloat(amount);
  if (isNaN(num)) {
    return '0,00';
  }
  const [intPart, decPart = '00'] = num.toFixed(2).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${decPart}`;
};

// formatBalanceLikeCard — теперь delegate к formatTokenAmount
// чтобы UI-балансы шли через ту же string-truncation что и Maximum-кнопка.
// Раньше функция использовала toFixed (округление вверх) — это давало
// рассинхрон: balance label показывал "3,06839508" а Maximum давал
// "3,06839507" для того же source-значения. Теперь оба пути truncate.
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

interface AccountPickerOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  tokenName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

interface OpenDepositScreenProps {
  balance?: V2Balance | null;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onDepositSuccess?: () => void;
  /** When true, parent supplies header + tabbar. */
  embedded?: boolean;
}

const OpenDepositScreen: React.FC<OpenDepositScreenProps> = ({
  balance,
  onBack,
  onNavigate,
  onDepositSuccess,
  embedded = false,
}) => {
  const [pickerOptions, setPickerOptions] = useState<AccountPickerOption[]>([]);
  const [selectedBalance, setSelectedBalance] = useState<V2Balance | null>(balance ?? null);
  const [pickerVisible, setPickerVisible] = useState(false);
  // Swipe-down close + top-flush inline sheet — тот же паттерн что у
  // CardHistoryPeriodScreen. Убрал Modal + fullscreen backdrop; picker
  // теперь absolute-positioned прямо под Currency-tile'ом. Swipe вниз
  // закрывает через useSwipeDownDismiss.
  const { translateY: pickerTranslateY, panHandlers: pickerPanHandlers } =
    useSwipeDownDismiss(pickerVisible, () => setPickerVisible(false));
  // Compounding info modal — opens on tap of the ? icon next to the
  // Compounding label. Pure informational bottom-sheet (no inputs).
  const [compoundInfoVisible, setCompoundInfoVisible] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(true);
  // Live token rates from /api/v2/rates, keyed by uppercase symbol. Drives
  // both the default-balance preselection (largest USDT-equiv first) and
  // the Min-amount validation gate (amount × rate ≥ min_amount_deposit).
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});

  const activeBalance = selectedBalance || balance || null;
  const currency = activeBalance?.token_symbol || 'USDT';

  const [depositAmount, setDepositAmount] = useState('');
  const [keypadVisible, setKeypadVisible] = useState(false);
  const [term, setTerm] = useState<number>(TERM_OPTIONS[0]);
  const [compounding, setCompounding] = useState<boolean>(true);

  // Live deposit catalog for the active currency. Drives:
  //   - rate / Expected income display (rate of the matched product)
  //   - which Term / Compounding pills are enabled (only combos that
  //     have at least one active row)
  //   - the FK we send on submit so the server stamps product_id with
  //     no resolution ambiguity
  const [products, setProducts] = useState<V2DepositProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per deposit: a press after a lost answer resends it with the
  // same key, and the server does not open it twice.
  const idempotency = useIdempotencyKey<V2CreateDepositRequest>();
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState<number>(0);
  const [submittedCurrency, setSubmittedCurrency] = useState<string>('');
  // Frozen contract snapshot at submit-time so the "Request sent" modal
  // shows the term/APY the user just confirmed, not whatever the screen
  // state holds afterwards (state could shift if the user keeps the
  // modal open and meanwhile changes term pills underneath).
  const [submittedTerm, setSubmittedTerm] = useState<number>(0);
  const [submittedApyFraction, setSubmittedApyFraction] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingBalances(true);
        // Fetch balances and rates in parallel — rates drive the default
        // preselection by largest USDT-equivalent value.
        const [balancesRes, ratesRes] = await Promise.all([
          v2GetMyBalances(),
          v2GetRates().catch(() => ({ rates: {} as Record<string, V2RateInfo> })),
        ]);
        if (cancelled) {
          return;
        }
        const liveRates = ratesRes.rates ?? {};
        setRates(liveRates);
        const allBalances = balancesRes.balances || [];
        const currentReal = allBalances.filter(
          (b) =>
            (b.account_type_code ?? 'current') === 'current' &&
            b.provider_name !== 'virtual',
        );
        const opts: AccountPickerOption[] = currentReal.map((b) => ({
          key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
          symbol: b.token_symbol,
          networkSymbol: b.network_symbol || '',
          networkName: b.network_name || '',
          tokenName: b.token_name || b.token_symbol,
          iconUrl: b.icon_url ?? null,
          amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
          balance: b,
        }));
        const tokenRank = (s: string) => (s.toUpperCase() === 'USDT' ? 0 : 1);
        opts.sort((a, b) => {
          const byNetwork = compareByNetwork(a, b);
          if (byNetwork !== 0) {
            return byNetwork;
          }
          const ta = tokenRank(a.symbol);
          const tb = tokenRank(b.symbol);
          if (ta !== tb) {
            return ta - tb;
          }
          return a.symbol.localeCompare(b.symbol);
        });
        setPickerOptions(opts);
        if (opts.length === 0) {
          setSelectedBalance(balance ?? null);
          return;
        }
        // Default preselection: explicit caller-supplied balance wins; then
        // a same-symbol fallback; otherwise pick the option with the
        // largest USDT-equivalent. `toUsdtAmount` returns null for tokens
        // with no live rate — treat those as 0 so they lose to anything
        // we can price.
        const byUsdtDesc = [...opts].sort(
          (a, b) =>
            (toUsdtAmount(b.amount, b.symbol, liveRates) ?? 0) -
            (toUsdtAmount(a.amount, a.symbol, liveRates) ?? 0),
        );
        const preselected =
          (balance && opts.find((o) => o.balance.id === balance.id)) ||
          (balance && opts.find((o) => o.symbol === balance.token_symbol)) ||
          byUsdtDesc[0];
        setSelectedBalance(preselected.balance);
      } catch (err) {
        console.warn('[OpenDeposit] Failed to load balances:', err);
        setPickerOptions([]);
        setSelectedBalance(balance ?? null);
      } finally {
        if (!cancelled) {
          setLoadingBalances(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [balance]);

  // Live "Minimum amount" threshold from v1_2_amount_settings.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetMinDepositAmount();
        const n = parseFloat(res.value);
        if (mounted && Number.isFinite(n) && n >= 0) {
          setMinAmount(n);
        }
      } catch {
        // Silent — row stays hidden if request fails.
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Load the catalog when the active currency changes. Filter and sort
  // are best applied client-side once: the slice we get back is small
  // (a few rows per currency) and we want to refresh on currency change
  // without round-trips for every term/compounding toggle.
  useEffect(() => {
    if (!currency) {
      return;
    }
    let cancelled = false;
    setProductsLoading(true);
    (async () => {
      try {
        const res = await v2GetDepositProducts(currency);
        if (cancelled) {
          return;
        }
        setProducts(res.products ?? []);
      } catch {
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setProductsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency]);

  // Highest-rate product for (term, compounding). Multiple matches are
  // allowed in the catalog (no UNIQUE constraint) — picking the best
  // rate matches the server-side `FindOneFor ORDER BY rate DESC` so the
  // value we show always equals what the server will charge.
  const selectedProduct = useMemo<V2DepositProduct | null>(() => {
    let best: V2DepositProduct | null = null;
    let bestRate = -Infinity;
    for (const p of products) {
      if (p.term !== term || p.compounding !== compounding) {
        continue;
      }
      const r = parseFloat(p.rate);
      if (!Number.isFinite(r)) {
        continue;
      }
      if (r > bestRate) {
        bestRate = r;
        best = p;
      }
    }
    return best;
  }, [products, term, compounding]);

  const apy = selectedProduct ? parseFloat(selectedProduct.rate) : null;

  // Sets of terms / compounding values that have at least one product
  // — used to grey out pill options the catalog can't honor.
  const availableTerms = useMemo<Set<number>>(() => {
    const s = new Set<number>();
    for (const p of products) {
      s.add(p.term);
    }
    return s;
  }, [products]);

  const compoundingAvailable = useMemo<{ yes: boolean; no: boolean }>(() => {
    let yes = false;
    let no = false;
    for (const p of products) {
      if (p.term !== term) {
        continue;
      }
      if (p.compounding) {
        yes = true;
      } else {
        no = true;
      }
    }
    return { yes, no };
  }, [products, term]);

  // Auto-correct the selection when a currency switch invalidates the
  // current (term, compounding) pair (e.g. ETH catalog has 6 mo but
  // not 3 mo). Prefer the existing values if still valid; otherwise
  // snap to the first product in the freshly loaded catalog.
  useEffect(() => {
    if (products.length === 0) {
      return;
    }
    const valid = products.some((p) => p.term === term && p.compounding === compounding);
    if (valid) {
      return;
    }
    const first = products[0];
    setTerm(first.term);
    setCompounding(first.compounding);
  }, [products]);

  const amount = parseAmount(depositAmount);
  const availableBalance =
    parseFloat(activeBalance?.available_amount ?? activeBalance?.amount ?? '0') || 0;

  // Min-amount is denominated in USDT (set in v1_2_amount_settings as
  // min_amount_deposit). Пересчёт в валюту выбранного current-balance'а
  // ("0.001 BTC" вместо "50 USDT"); unknown rate → no minimum shown or
  // checked (fail-open), as before.
  //
  // The hint and the check read the same figure: the threshold in the token,
  // rounded UP to the places the hint shows. The hint used to show the float
  // quotient, cut to its display places, while the check compared the USDT
  // value — so the very figure on screen could be refused. Withdraw does the
  // same (minimumAmountRule).
  const minAmountInCurrency: number | null = (() => {
    if (minAmount == null || !activeBalance) return null;
    const sym = activeBalance.token_symbol;
    const price = isUsdPegged(sym)
      ? 1
      : parseFloat((rates[sym.toUpperCase()] ?? rates[sym])?.price_usd ?? '');
    if (!Number.isFinite(price) || price <= 0) return null;
    const raw = minAmount / price;
    // formatTokenAmount shows fewer places the larger the figure; round up
    // to no more than it will show, so nothing is cut off the hint.
    const shown = raw >= 1000 ? 2 : raw >= 1 ? 6 : 8;
    const places = Math.min(activeBalance.token_decimals ?? 2, MAX_AMOUNT_DECIMALS, shown);
    return minimumInToken(minAmount, price, places);
  })();
  const meetsMinimum = minAmountInCurrency == null
    || Math.round(amount * 1e8) >= Math.round(minAmountInCurrency * 1e8);
  const isValidAmount = amount > 0 && amount <= availableBalance && meetsMinimum;

  const canSubmit =
    isValidAmount &&
    !loadingBalances &&
    !!activeBalance &&
    !!selectedProduct;

  // Expected income — simple interest for Simple, annual-compounding for
  // Compound. Both formulas reduce to 0 when amount or apy is zero.
  const termYears = term / 12;
  const expectedIncome = apy != null && amount > 0
    ? (compounding
        ? amount * (Math.pow(1 + apy, termYears) - 1)
        : amount * apy * termYears)
    : 0;

  const handleMaximum = () => {
    // Передаём STRING напрямую (минуя availableBalance number) чтобы не
    // потерять точность через parseFloat — "3.06839508" в JS Number
    // хранится как 3.0683950799999998 и toString даёт "...07999..." →
    // string-slice уносит последнюю цифру. См. numberToAmountDisplay docstring.
    const decimals = Math.min(activeBalance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS);
    const balanceStr = activeBalance?.available_amount ?? activeBalance?.amount ?? '0';
    setDepositAmount(numberToAmountDisplay(balanceStr, decimals));
  };

  const handleSelectOption = (opt: AccountPickerOption) => {
    setSelectedBalance(opt.balance);
    setPickerVisible(false);
  };

  const handleConfirm = () => {
    if (!canSubmit || !activeBalance || isSubmitting) {
      return;
    }
    if (!isPINSetup || isPINSkipped) {
      executeConfirm();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeConfirm = async () => {
    setPinModalVisible(false);
    if (!activeBalance) {
      return;
    }
    setIsSubmitting(true);
    const request: V2CreateDepositRequest = {
      balance_id: activeBalance.id,
      amount: toRequestAmount(depositAmount),
      term,
      compounding,
      product_id: selectedProduct?.id,
    };
    try {
      const attempt = idempotency.attempt(request);
      const deposit = await v2CreateDeposit(attempt.request, attempt.key);
      idempotency.answered();
      console.log('Deposit created:', deposit.id, 'Status:', deposit.status_name);
      setSubmittedAmount(amount);
      setSubmittedCurrency(currency);
      setSubmittedTerm(term);
      setSubmittedApyFraction(apy ?? 0);
      setSuccessModalVisible(true);
    } catch (error) {
      idempotency.failed(error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      Alert.alert('Deposit Failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activePickerOption =
    pickerOptions.find((o) => o.balance.id === activeBalance?.id) ?? null;
  const pickerTitle = activePickerOption
    ? activePickerOption.networkSymbol
      ? `${activePickerOption.symbol} - ${activePickerOption.networkSymbol}`
      : activePickerOption.symbol
    : currency;
  const pickerSubtitle = activePickerOption?.networkName || '';

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      {!embedded && (
        <SimpleHeader title="Open deposit" onBack={onBack} backgroundVariant="primaryNavy" />
      )}

      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <KeyboardAvoidingView
          style={styles.keyboardView}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Tap-anywhere-to-close wrap for the inline NumericKeypad.
                Mirrors DepositCardScreen: nested TouchableOpacity tap-
                targets absorb their own touches, so this Pressable only
                fires on inert regions. Closes the keypad without a
                backdrop overlay (no Modal). */}
            <Pressable onPress={() => setKeypadVisible(false)} accessible={false}>
            {/* Currency + Term + Compounding — one outer card */}
            <View style={styles.outerCard}>
              <Text style={styles.outerCardLabel}>Currency</Text>
              {loadingBalances ? (
                <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
                  <ActivityIndicator size="small" color={COLORS.brandOrange} />
                </View>
              ) : (
                <TouchableOpacity
                  style={styles.networkSelector}
                  onPress={() => setPickerVisible(true)}
                  activeOpacity={0.7}
                >
                  <TokenIcon
                    symbol={currency}
                    iconUrl={activeBalance?.icon_url ?? null}
                    style={styles.networkSelectorIcon}/>
                  <View style={styles.networkTextContainer}>
                    <Text style={styles.networkName}>{pickerTitle}</Text>
                    {pickerSubtitle ? (
                      <Text style={styles.networkSubtitle}>{pickerSubtitle}</Text>
                    ) : null}
                  </View>
                  <Image
                    source={require('../../../assets/arrow_left_20.png')}
                    style={styles.chevronIcon}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              )}

              {/* Term */}
              <Text style={[styles.outerCardLabel, styles.outerCardLabelInline]}>Term</Text>
              <View style={styles.termRow}>
                {TERM_OPTIONS.map((t) => {
                  const isSelected = t === term;
                  const isDisabled = products.length > 0 && !availableTerms.has(t);
                  return (
                    <TouchableOpacity
                      key={t}
                      style={[
                        styles.termPill,
                        isSelected && styles.termPillSelected,
                        isDisabled && styles.termPillDisabled,
                      ]}
                      activeOpacity={isDisabled ? 1 : 0.7}
                      onPress={() => {
                        if (isDisabled) {
                          return;
                        }
                        setTerm(t);
                      }}
                    >
                      <Text
                        style={[
                          styles.termPillText,
                          isSelected && styles.termPillTextSelected,
                          isDisabled && styles.termPillTextDisabled,
                        ]}
                      >
                        {t} month
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {/* Compounding — Yes / No */}
              <View style={styles.compoundLabelRow}>
                <Text style={styles.outerCardLabel}>Compounding</Text>
                {/* Tap on the ? glyph opens an info bottom-sheet
                    explaining what compounding does. hitSlop widens
                    the tap area to a comfortable size — the icon
                    itself is only 16dp. */}
                <TouchableOpacity
                  onPress={() => setCompoundInfoVisible(true)}
                  activeOpacity={0.6}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Image
                    source={require('../../../assets/question.png')}
                    style={styles.compoundInfoIcon}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.compoundRow}>
                {[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ].map((opt) => {
                  const isSelected = opt.value === compounding;
                  const isDisabled =
                    products.length > 0 &&
                    (opt.value ? !compoundingAvailable.yes : !compoundingAvailable.no);
                  return (
                    <TouchableOpacity
                      key={opt.label}
                      style={[
                        styles.compoundPill,
                        isSelected && styles.termPillSelected,
                        isDisabled && styles.termPillDisabled,
                      ]}
                      activeOpacity={isDisabled ? 1 : 0.7}
                      onPress={() => {
                        if (isDisabled) {
                          return;
                        }
                        setCompounding(opt.value);
                      }}
                    >
                      <Text
                        style={[
                          styles.termPillText,
                          isSelected && styles.termPillTextSelected,
                          isDisabled && styles.termPillTextDisabled,
                        ]}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* Amount — separate card */}
            <TouchableOpacity
              style={[styles.inputContainer, styles.amountContainerSpacing]}
              activeOpacity={0.7}
              onPress={() => !isSubmitting && setKeypadVisible(true)}
              disabled={isSubmitting}
            >
              <Text style={styles.amountLabel}>Enter amount</Text>
              {/* Amount value and currency suffix are TWO separate Text
                  nodes so the blinking cursor sits right after the
                  number — not after "USDT". When empty, value renders
                  "0" and suffix renders " USDT".
                  Invalid amount (превышение баланса / меньше minimum)
                  подкрашивается в #CD0A0A — см. amountInputInvalid. */}
              <Text style={[
                styles.textInput,
                styles.amountInput,
                amount > 0 && !isValidAmount ? styles.amountInputInvalid : null,
              ]}>
                {depositAmount || '0'}
              </Text>
              <AmountCaret active={keypadVisible} />
              <Text style={[
                styles.amountSuffix,
                amount > 0 && !isValidAmount ? styles.amountSuffixInvalid : null,
              ]}>{currency}</Text>
            </TouchableOpacity>

            {/* Min amount + Available for deposit card.
                Blends with the screen background (pureLight, no border)
                so the numbers read as informational notes rather than a
                separate tile. Numbers are 16/400 to match the surrounding
                gray labels. */}
            <View style={[styles.infoCard, styles.infoCardSpacing, styles.infoCardFlat]}>
              <View style={styles.infoCardRow}>
                <Text style={styles.amountLabel}>Minimum amount:</Text>
                <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                  {minAmountInCurrency != null
                    ? `${formatBalanceLikeCard(minAmountInCurrency, activeBalance?.token_decimals)} ${currency}`
                    : formatUsdtAmount(minAmount, 0)}
                </Text>
              </View>
              <View style={styles.infoCardDivider} />
              <View style={styles.infoCardRow}>
                <Text style={styles.amountLabel}>Available for deposit:</Text>
                <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                  {formatBalanceLikeCard(
                    activeBalance?.available_amount ?? activeBalance?.amount ?? '0',
                    activeBalance?.token_decimals,
                  )} {currency}
                </Text>
              </View>
            </View>

            {/* APY + Expected income card */}
            <View style={[styles.infoCard, styles.infoCardSpacing]}>
              <View style={styles.infoCardRow}>
                <Text style={styles.apyLabel}>% APY</Text>
                <Text style={styles.apyValue}>
                  {apy != null ? `${(apy * 100).toFixed(2).replace(/\.?0+$/, '')}%` : '—'}
                </Text>
              </View>
              <View style={styles.infoCardDivider} />
              <View style={styles.infoCardRow}>
                <Text style={styles.amountLabel}>Expected income</Text>
                <Text style={[styles.amountInput, styles.feeAmount]}>
                  {formatBalanceLikeCard(expectedIncome, activeBalance?.token_decimals)} {currency}
                </Text>
              </View>
            </View>

            {/* Confirm button — Primary/Navy */}
            <TouchableOpacity
              style={[
                styles.transferButton,
                (!canSubmit || isSubmitting) && styles.transferButtonDisabled,
              ]}
              onPress={handleConfirm}
              activeOpacity={canSubmit && !isSubmitting ? 0.8 : 1}
              disabled={!canSubmit || isSubmitting}
            >
              <Text
                style={[
                  styles.transferButtonText,
                  (!canSubmit || isSubmitting) && styles.transferButtonTextDisabled,
                ]}
              >
                {isSubmitting ? 'Processing...' : 'Open'}
              </Text>
            </TouchableOpacity>

            {/* Catalog-availability hint. Surfaces only when the loader has
                settled — keeps the row from flashing while products fetch. */}
            {!productsLoading && products.length === 0 ? (
              <Text style={styles.noProductHint}>
                No deposit product available for this currency
              </Text>
            ) : null}

            <View style={{ height: 40 }} />
            </Pressable>
          </ScrollView>
        </KeyboardAvoidingView>
      </TouchableWithoutFeedback>

      {/* Balance picker bottom-sheet — inline (без Modal + без fullscreen
          backdrop'а). absolute-positioned под Currency-tile'ом через
          PICKER_TOP_INSET; закрывается свайпом вниз через
          useSwipeDownDismiss. Тот же паттерн что у CardHistoryPeriodScreen. */}
      {/* Web: the sheet sits low on a dimmed backdrop that closes it (web/sheet.ts). */}
      {pickerVisible && Platform.OS === 'web' && (
        <Pressable
          style={[StyleSheet.absoluteFill, WEB_SHEET_BACKDROP, styles.webPickerBackdrop]}
          onPress={() => setPickerVisible(false)}
        />
      )}
      {pickerVisible && (
        <Animated.View
          style={[
            styles.modalContent,
            WEB_SHEET && [WEB_SHEET, styles.webPickerSheet],
            { transform: [{ translateY: pickerTranslateY }] },
          ]}
          {...pickerPanHandlers}
        >
          <View style={styles.modalHandle} />
          <View style={styles.modalTitleRow}>
            <Text style={styles.modalTitle}>Current accounts</Text>
            <Text style={styles.modalTitleAside}>Available balance</Text>
          </View>
          {pickerOptions.length === 0 ? (
            <Text style={[styles.modalOptionSubtitle, { paddingHorizontal: 12 }]}>
              No current-account balances available.
            </Text>
          ) : null}
          {pickerOptions.map((opt) => {
            const isSelected = activeBalance?.id === opt.balance.id;
            const title = opt.networkSymbol
              ? `${opt.symbol} - ${opt.networkSymbol}`
              : opt.symbol;
            return (
              <TouchableOpacity
                key={opt.key}
                style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                onPress={() => handleSelectOption(opt)}
                activeOpacity={0.7}
              >
                <TokenIcon
                  symbol={opt.symbol}
                  iconUrl={opt.iconUrl}
                  style={styles.modalOptionIcon}/>
                <View style={styles.modalOptionTextContainer}>
                  <Text style={styles.modalOptionName}>{title}</Text>
                  {opt.networkName ? (
                    <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                  ) : null}
                </View>
                <Text style={styles.modalOptionAmount}>
                  {formatBalanceLikeCard(opt.amount, opt.balance.token_decimals)} {opt.symbol}
                </Text>
              </TouchableOpacity>
            );
          })}
        </Animated.View>
      )}

      {!embedded && <TabBar activeTab="dashboard" onNavigate={onNavigate} />}

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm deposit"
        subtitle={`Open ${formatAmount(depositAmount || '0', MAX_AMOUNT_DECIMALS)} ${currency} · ${term} months`}
        onSuccess={executeConfirm}
        onCancel={() => setPinModalVisible(false)}
      />

      <NumericKeypad
        inline
        visible={keypadVisible}
        value={depositAmount}
        // The token's places, eight at most: 0,015 BTC could not be typed at two.
        onChange={(next) => setDepositAmount(
          formatAmount(next, Math.min(activeBalance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS)),
        )}
        onClose={() => setKeypadVisible(false)}
        availableValue={`${formatBalanceLikeCard(
          activeBalance?.available_amount ?? activeBalance?.amount ?? '0',
          activeBalance?.token_decimals,
        )} ${currency}`}
        onMax={handleMaximum}
        onClean={() => setDepositAmount('')}
      />

      {/* "Request sent" modal — shown after the user submits a
          deposit. The application is now pending admin confirm; this
          screen communicates that intermediate state. Closing the
          modal (X or tap outside) navigates back to the dashboard. */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => {
          setSuccessModalVisible(false);
          onDepositSuccess?.();
          onBack();
        }}
      >
        <Pressable
          style={styles.successOverlay}
          onPress={() => {
            setSuccessModalVisible(false);
            onDepositSuccess?.();
            onBack();
          }}
        >
          <Pressable
            style={styles.successCard}
            onPress={(e) => e.stopPropagation()}
          >
            {/* X close button — top-right corner */}
            <TouchableOpacity
              style={styles.successCloseBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              onPress={() => {
                setSuccessModalVisible(false);
                onDepositSuccess?.();
                onBack();
              }}
              activeOpacity={0.6}
            >
              <Text style={styles.successCloseGlyph}>×</Text>
            </TouchableOpacity>

            <Image
              source={require('../../../assets/percentage.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Request sent</Text>
            <Text style={styles.successSubtitle}>
              Operation is being processed.
            </Text>
            {/* Amount row — открытие депозита это pending-заявка, а не
                fait-accompli debit. До admin confirm'а знак минус
                вводил в заблуждение (юзер видел «−100» и думал что
                списание уже прошло). Показываем сумму без знака —
                актуальное движение отобразится в history после confirm'а. */}
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>{`${formatAmount(depositAmount || '0', MAX_AMOUNT_DECIMALS)} `}</Text>
              <Text style={styles.successCurrency}>{currency}</Text>
            </View>

            {/* Explicit primary CTA — mirrors the tap-outside / X-close
                behaviour but gives the user a clear "I'm done" affordance
                without having to find the corner X. Matches the navy
                CTA styling used on Withdraw / TopUp / Open deposit. */}
            <TouchableOpacity
              style={styles.successOkButton}
              activeOpacity={0.75}
              onPress={() => {
                setSuccessModalVisible(false);
                onDepositSuccess?.();
                onBack();
              }}
            >
              <Text style={styles.successOkButtonText}>Ok</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Compounding info bottom-sheet. Tapping outside (overlay) or
          using the OS back-gesture closes it. Pure informational —
          no buttons; the user just dismisses when done reading. */}
      <Modal
        visible={compoundInfoVisible}
        transparent
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setCompoundInfoVisible(false)}
      >
        <Pressable
          style={styles.infoOverlay}
          onPress={() => setCompoundInfoVisible(false)}
        >
          <Pressable
            style={styles.infoSheet}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.infoHandle} />
            <Text style={styles.infoTitle}>Compounding</Text>
            <Text style={styles.infoParagraph}>
              Interest is added to your deposit balance and earns interest in the next period.
            </Text>
            <Text style={styles.infoParagraph}>
              If disabled, interest is paid to your current account at maturity.
            </Text>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    paddingTop: 16,
    // 16 dp left/right matches WithdrawScreen / DepositCardScreen /
    // ConfirmDepositScreen — single screen-edge padding across all
    // Operations tabs.
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  outerCard: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginBottom: 16,
  },
  outerCardLabel: {
    fontFamily: 'Inter',
    // Inter 16 / 400 / lh 17 / blueSteel — full match with
    // WithdrawScreen.sectionLabel and DepositCardScreen.pickerLabel.
    // Used for "Currency", "Term" and "Compounding" labels inside
    // the outer card.
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  outerCardLabelInline: {
    marginTop: 4,
  },
  networkSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  networkSelectorIcon: {
    // 28 dp matches DepositCardScreen.tokenIcon / WithdrawScreen
    // .networkSelectorIcon — unified icon size in the "From current
    // account" picker tile across Operations tabs.
    width: 28,
    height: 28,
    marginRight: 10,
  },
  networkTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  networkName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  networkSelectorCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    // 16 dp gap to the next element (Min/Available info card) — uniform
    // with the rest of the stack. Was 8.
    marginBottom: 16,
  },
  amountContainerSpacing: {
    marginTop: 0,
  },
  amountLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    // Bumped to 16 dp so "Enter amount" matches WithdrawScreen.amountLabel
    // and DepositCardScreen.pickerLabel — same gray label typography
    // across all three Operations screens.
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  amountInput: {
    textAlign: 'right',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  // Invalid amount styles — применяются когда amount > 0 и не проходит
  // isValidAmount (превышение баланса source / меньше minimum). Цвет
  // #CD0A0A — отличается от alertRed warning bar'а, см. amountInvalid
  // в colorsWrong.ts.
  amountInputInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  amountSuffix: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.steelNavy,
  },
  amountSuffixInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  /* Blinking caret next to the amount input — 2-dp wide vertical line
     matching the amount text baseline. Visibility flips on a 500 ms
     tick via the local <BlinkingCursor> component. */
  textInput: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
    color: COLORS.steelNavy,
    padding: 0,
  },
  termRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  termPill: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    backgroundColor: COLORS.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  termPillSelected: {
    borderColor: COLORS.brandOrange,
    backgroundColor: COLORS.brandOrange,
  },
  termPillText: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  termPillTextSelected: {
    color: COLORS.white,
  },
  termPillDisabled: {
    backgroundColor: COLORS.softGrey,
    borderColor: COLORS.borderMuted,
    opacity: 0.55,
  },
  termPillTextDisabled: {
    color: COLORS.blueSteel,
  },
  compoundLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  compoundInfoIcon: {
    width: 16,
    height: 16,
    marginLeft: 6,
    marginBottom: 8,
    tintColor: COLORS.blueSteel,
  },
  compoundRow: {
    flexDirection: 'row',
    gap: 8,
  },
  /* Compounding pills are not full-width — two narrower pills like in the
     wireframe (Yes / No), justified to the start. */
  compoundPill: {
    width: 110,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    backgroundColor: COLORS.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoCardSpacing: {
    marginBottom: 16,
  },
  apyLabel: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  apyValue: {
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 18,
    lineHeight: 22,
    color: '#2E7D32',
    textAlign: 'right',
  },
  feeAmount: {
    flex: 1,
    color: COLORS.blueSteel,
  },
  infoCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  /* "Flat" variant — used for the Minimum amount / Available block so
     it blends with the pureLight screen background instead of standing
     up as a white tile. Keeps the muted border outline and the inner
     divider so the block stays framed. */
  infoCardFlat: {
    backgroundColor: COLORS.pureLight,
  },
  /* Regular-weight override for numbers in the flat info card — the
     base `amountInput` is 16/500 (medium), `feeAmountRegular` brings
     it down to 16/400 so the values read as gray informational text
     rather than emphasized input. */
  feeAmountRegular: {
    fontWeight: '400',
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  infoCardDivider: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
  },
  transferButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 12,
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  transferButtonDisabled: {
    backgroundColor: COLORS_WRONG.disabled,
  },
  transferButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  transferButtonTextDisabled: {
    color: COLORS.white,
  },
  noProductHint: {
    marginTop: 12,
    textAlign: 'center',
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: '400',
    color: COLORS.blueSteel,
  },
  modalContent: {
    // Inline absolute-positioned sheet — pin'ится верхом к Currency-tile
    // через PICKER_TOP_INSET, растягивается до низа. Заменил старый
    // Modal + flex:1 layout: теперь sheet живёт в родительском coordinate
    // space без fullscreen backdrop'а (см. CardHistoryPeriodScreen для
    // такого же паттерна). zIndex/elevation держат его поверх content'а
    // и TabBar'а.
    position: 'absolute',
    left: 0,
    right: 0,
    top: PICKER_TOP_INSET,
    bottom: 0,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
    zIndex: 10,
    elevation: 10,
  },
  webPickerBackdrop: { zIndex: 10 },
  // Anchored to the bottom only; WEB_SHEET caps the height.
  webPickerSheet: { top: 'auto' },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  // Title style aligned with TopUp/Outflow/Confirm picker'ами — был
  // outlier'ом (20/600 + marginBottom) который ломал consistency. Сейчас
  // 16/400/lineHeight=20 поверх primaryNavy + blueSteel для aside. Parent
  // modalTitleRow владеет vertical-spacing'ом, marginBottom тут не нужен.
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionIcon: {
    // 28 dp matches the picker tile + the equivalent icons on Top-up /
    // Withdraw / Inflow — single icon size across Operations tabs.
    width: 28,
    height: 28,
    marginRight: 10,
  },
  modalOptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  checkmark: {
    fontSize: 20,
    color: COLORS.mint,
    fontWeight: '600',
  },
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 48,        // extra top for the X-close button + icon
    // 24 — gives the new "Ok" CTA breathing room from the card edge
    // without leaving an oversized hollow that the old (no-button)
    // card's 56 dp needed for visual balance.
    paddingBottom: 24,
    alignItems: 'center',
  },
  /* X close button — top-right of the card, separate from the icon
     stack so the overlay-tap-to-close is mirrored by an explicit
     button for users who prefer it. */
  successCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCloseGlyph: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: '400',
    color: COLORS.steelNavy,
  },
  successIcon: {
    width: 64,
    height: 64,
    marginBottom: 28,
  },
  successTitle: {
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 32,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 6,
  },
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: 24,
  },
  // Финальный эскиз использует dark navy для amount (был mint в ранней
  // версии — стили не удалены, перекрашены). Currency на 0.6 opacity'ом
  // для иерархии amount > currency, оба остаются navy для consistency
  // с title.
  successAmount: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: '700',
    lineHeight: 42,
    color: COLORS.textPrimary,
  },
  successCurrency: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.blueSteel,
  },
  /* Contract caption — "USDT · 3 months · 4% APY". Smaller, gray-dark,
     centered, sits below the amount row. */
  successContractLine: {
    marginTop: 16,
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  /* Primary "Ok" CTA at the bottom of the success card. Stretches the
     card's content width so it reads as a deliberate close affordance. */
  successOkButton: {
    alignSelf: 'stretch',
    marginTop: 32,
    height: 48,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successOkButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  /* Compounding info bottom-sheet — semi-opaque overlay over the
     screen, white sheet pinned to the bottom with rounded top corners.
     Matches the prototype: small handle bar, bold title, two paragraph
     bodies. No buttons — tap-overlay to dismiss. */
  infoOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    justifyContent: 'flex-end',
  },
  infoSheet: {
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 32,
  },
  infoHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  infoTitle: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '600',
    lineHeight: 28,
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  infoParagraph: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.steelNavy,
    marginBottom: 12,
  },
});

export default OpenDepositScreen;
